//ClientApp\src\apps\components\mapViewers\WellArcGISMapper\hooks\helpers\bindCellEvents.js
import { LOG_MODULES, APP_CONFIG } from "@config/config";
import { log } from '@libs/utils/helper.js';
import { zoomToWells, toggleSelection } from './utils';

const logFtTblDug = LOG_MODULES.FEATURETABLBE;
const zoomForDetail = APP_CONFIG.MAPS.Normal.zoomForDetail;
const isNeedLog = false;

// CONFIRMED against official component docs (Events table):
// arcgisCellClick: CustomEvent<TableInteractionCellClickEvent>
// arcgisCellDblClick: CustomEvent<TableInteractionCellDblclickEvent>
// Note: it's "Dbl", not "Double" — that was wrong in an earlier draft.
const CELL_CLICK_EVENT = "arcgisCellClick";
const CELL_DBLCLICK_EVENT = "arcgisCellDblClick";

export function bindCellEvents({ table, mapView, highlightLayer, handlersRef }) {
  const targetLayer = table.layer;

  const performZoom = async (api_number) => {
    try {
      await zoomToWells(mapView, targetLayer, api_number, true, highlightLayer, zoomForDetail);
    } catch (err) {
      console.error("[Table Click] Zoom Error:", err);
    }
  };

  const getFeatureIds = (feature) => {
    const attrs = feature?.attributes ?? {};
    return {
      api_number: attrs["API_NUMBER"]?.slice(0, 10),
      well_id: (attrs[APP_CONFIG.MAPS.Well.queryField] ?? "").replace(/[{}]/g, ""),
      object_id: attrs[targetLayer.object_idField || "OBJECTID"]
    };
  };

  /* =========================================================
   Checkbox branch keeps letting checkedRds state drive
   syncTableHighlights (via the effect in useWellFeatureTable.js)
   as the single source of truth — same intent as before.
   ========================================================= */

  const handleCellClick = async (nativeEvent) => {
    // Native CustomEvents nest the payload under .detail.
    // If this logs undefined, the payload shape has changed — log
    // nativeEvent itself to see what's actually there.
    const event = nativeEvent.detail ?? nativeEvent;
    const feature = event?.feature;
    if (!feature) return;

    const { well_id, object_id } = getFeatureIds(feature);
    const uniqeID = well_id;

    const currentCheckedRds = handlersRef.current.checkedRds || [];
    const isIncluded = currentCheckedRds.includes(uniqeID);
    const isCheckboxClick = !event.fieldName;

    if (isNeedLog) log(logFtTblDug, "bindCellEvents [cell-click] isCheckboxClick:", isCheckboxClick, event.fieldName);

    if (isCheckboxClick) {
      if (isNeedLog) log(logFtTblDug, "bindCellEvents [Checkbox Click] isIncluded:", isIncluded);

      if (isIncluded) {
        // --- DESELECT ---
        table.highlightIds.remove(object_id);
      } else {
        // --- SELECT ---
        table.highlightIds.add(object_id);
        await performZoom(uniqeID);
      }
      toggleSelection(uniqeID, handlersRef);
      return;
    }

    // Non-checkbox cell clicked — just zoom to that point
    await performZoom(uniqeID);
  };

  const handleCellDblClick = async (nativeEvent) => {
    const event = nativeEvent.detail ?? nativeEvent;
    const feature = event?.feature;
    if (!feature) return;

    const { api_number, well_id, object_id } = getFeatureIds(feature);

    if (!table.highlightIds.includes(object_id)) {
      table.highlightIds.add(object_id);
    }

    handlersRef.current.onDoubleClick?.({
      api_number,
      id: well_id,
      is_prod_group: false
    });
    handlersRef.current.onCloseFeaturesPanel?.();
    await performZoom(well_id);
  };

  table.addEventListener(CELL_CLICK_EVENT, handleCellClick);
  table.addEventListener(CELL_DBLCLICK_EVENT, handleCellDblClick);

  return () => {
    table.removeEventListener(CELL_CLICK_EVENT, handleCellClick);
    table.removeEventListener(CELL_DBLCLICK_EVENT, handleCellDblClick);
  };
}